// The glass appearance's own settings: the colour of the glow, how strong it
// is, how much the panels blur, and a few named presets. Pure, so the store, the
// Shell and the tests share the clamps. Opt-in: nothing here is read unless the
// device is on glass.
import { GLASS } from './colour'
import { HUES, type Hue } from './colour'

/** `suite` follows the suite's (or Everything's) own accent, as glass always has. */
export type GlassTint = 'suite' | Hue

export type GlassLook = {
  tint: GlassTint
  /** 0–1 of the accent in the brightest blob; capped at GLASS.glowMax. */
  glow: number
  /** backdrop blur in px */
  blur: number
}

export const GLASS_BLUR = { min: 6, max: 40 } as const

export const DEFAULT_GLASS_LOOK: GlassLook = { tint: 'suite', glow: GLASS.glow, blur: GLASS.blur }

export const GLASS_PRESETS: { key: string; label: string; hint: string; look: GlassLook }[] = [
  { key: 'default', label: 'Glass', hint: 'The suite’s colour, a clear glow', look: DEFAULT_GLASS_LOOK },
  {
    key: 'aurora',
    label: 'Aurora',
    hint: 'Cool green light, wide and soft',
    look: { tint: 'emerald', glow: 0.36, blur: 30 },
  },
  {
    key: 'ember',
    label: 'Ember',
    hint: 'Warm amber, close to the glass',
    look: { tint: 'orange', glow: 0.38, blur: 18 },
  },
  {
    key: 'deep-sea',
    label: 'Deep sea',
    hint: 'Teal depth, heavily frosted',
    look: { tint: 'teal', glow: 0.32, blur: 36 },
  },
  {
    key: 'orchid',
    label: 'Orchid',
    hint: 'Magenta haze, a lighter frost',
    look: { tint: 'fuchsia', glow: 0.34, blur: 14 },
  },
  {
    key: 'moonlight',
    label: 'Moonlight',
    hint: 'Silver and quiet, barely there',
    look: { tint: 'slate', glow: 0.18, blur: 26 },
  },
]

const num = (v: unknown, lo: number, hi: number, fallback: number) =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : fallback

/** Whatever was stored, made safe: a bad value falls back to the default one field at a time. */
export const sanitizeGlassLook = (raw: unknown): GlassLook => {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const tint = o.tint === 'suite' || HUES.includes(o.tint as Hue) ? (o.tint as GlassTint) : 'suite'
  return {
    tint,
    glow: num(o.glow, 0, GLASS.glowMax, GLASS.glow),
    blur: Math.round(num(o.blur, GLASS_BLUR.min, GLASS_BLUR.max, GLASS.blur)),
  }
}

/** The preset a look equals, if any. */
export const presetOf = (look: GlassLook) =>
  GLASS_PRESETS.find(
    (p) =>
      p.look.tint === look.tint && Math.abs(p.look.glow - look.glow) < 0.005 && p.look.blur === look.blur,
  )
